import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";

const MARQUEE = ["Technology", "Efficiency", "Decisions", "Smarter Workflows", "Stronger Businesses", "Businesses"];

function BrandPanel() {
  const items = [...MARQUEE, ...MARQUEE];
  return (
    <div className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-[#070a1f] text-white" data-testid="login-brand-panel">
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-indigo-900/30 blur-3xl" />
      <div className="absolute top-1/4 right-0 h-72 w-72 rounded-full bg-fuchsia-900/20 blur-3xl" />
      <div className="relative p-9">
        <div className="flex items-start font-heading font-extrabold text-4xl tracking-tight" data-testid="login-brand-logo">
          <span className="text-white">T</span>
          <span className="bg-gradient-to-r from-sky-400 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">ED</span>
          <span className="text-white font-semibold ml-0.5">Ops</span>
          <sup className="text-xs font-bold text-fuchsia-400 mt-1 ml-0.5">AI</sup>
        </div>
        <p className="mt-3 text-[10px] tracking-[0.35em] uppercase text-slate-500 font-mono">Technology. Efficiency. Decisions.</p>
      </div>

      <div className="relative px-9">
        <h1 className="font-heading text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight">
          Smarter <span className="bg-gradient-to-r from-sky-400 to-fuchsia-500 bg-clip-text text-transparent">Workflows.</span>
          <br />
          Stronger
          <br />
          <span className="bg-gradient-to-r from-cyan-400 via-indigo-400 to-violet-500 bg-clip-text text-transparent">Businesses.</span>
        </h1>
        <p className="mt-8 text-slate-400 max-w-xs leading-relaxed">AI-powered operations software for real-world businesses.</p>
      </div>

      <div className="relative">
        <div className="relative h-48 overflow-hidden">
          <div className="absolute left-1/2 top-6 -translate-x-1/2 h-[900px] w-[140%] rounded-[50%] border-t-2 border-cyan-400/70 shadow-[0_-8px_40px_rgba(34,211,238,0.25)]" />
        </div>
        <div className="border-t border-white/5 py-4 overflow-hidden">
          <div className="flex w-max animate-marquee gap-10 text-[10px] tracking-[0.35em] uppercase text-slate-500 font-mono">
            {items.map((t, i) => (
              <span key={i} className="flex items-center gap-10">
                {t} <span className="text-indigo-400">◆</span>
              </span>
            ))}
          </div>
        </div>
        <p className="px-9 pb-5 text-[10px] tracking-[0.3em] uppercase text-slate-600 font-mono">© {new Date().getFullYear()} TED Ops AI</p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3 h-11 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100 transition-colors">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      {children}
    </div>
  );
}

export default function LoginPage() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[42%_58%]">
      <BrandPanel />
      <div className="flex items-center justify-center px-5 py-12 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-100 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)] p-8 animate-fade-in-up" data-testid="login-card">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1 mb-7">Sign in to your workspace.</p>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-semibold text-slate-900">Email address</label>
              <Field icon={Mail}>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400" data-testid="login-email-input" />
              </Field>
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-semibold text-slate-900">Password</label>
              <Field icon={Lock}>
                <input id="password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400" data-testid="login-password-input" />
                <button type="button" onClick={() => setShow((v) => !v)} className="text-slate-400 hover:text-slate-700 transition-colors" aria-label="Toggle password visibility" data-testid="login-toggle-password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </Field>
            </div>
            {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full h-11 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-violet-500/25 hover:brightness-110 active:scale-[0.98] transition-[filter,transform] disabled:opacity-70" data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Lock className="h-4 w-4" /> Sign in <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
